import { InformationButton } from "@components/buttons/information";
import { SHTextField } from "@components/design-systems";
import {
  useEditESGPaths,
  useInputDotIndicator,
} from "@hooks/useInvestmentProduct";
import { InvestmentProductEsgDTO } from "@models/product/investment-product/entities/esg";
import { debounce } from "lodash";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  EditESGInputControllerProps,
  handleOnEditESGFieldChange,
} from "../../util";
import { EditProductDefaults } from "@pages/suppliers/_id/_products/investment-product/_id/edit/constants";

export const EditESGShortTextInput = ({
  featureData,
  featureIndex,
  sectionIndex,
  isDisabled,
  onChange,
}: EditESGInputControllerProps) => {
  const { control, getValues, setValue } =
    useFormContext<InvestmentProductEsgDTO>();

  const { featureProductDataPath } = useEditESGPaths(
    sectionIndex,
    featureIndex,
  );

  const { isShowOrangeDot, isShowBlueDot } =
    useInputDotIndicator<InvestmentProductEsgDTO>({
      getValues,
      control,
      paths: {
        highlightType: featureProductDataPath.highlightType,
        dataStatus: featureProductDataPath.dataStatus,
      },
    });

  const postfixLabel = useMemo(() => {
    return !!featureData.description ? (
      <InformationButton content={featureData.description} />
    ) : undefined;
  }, [featureData.description]);

  const handleOnChange = (newValue: string | undefined) => {
    handleOnEditESGFieldChange({
      originalValue: featureData.productData?.value,
      newValue,
      getValues,
      setValue,
      featureProductDataPath,
    });

    onChange && onChange({ sectionIndex, featureIndex, newValue });
  };

  const debouncedHandleInputChange = debounce(
    handleOnChange,
    EditProductDefaults.DebounceTime,
  );

  return (
    <Controller
      name={featureProductDataPath.value}
      control={control}
      render={({ field: { ref, ...other } }) => (
        <SHTextField
          {...other}
          disabled={isDisabled}
          sx={{ width: { xs: "100%", md: 520, sm: 520 } }}
          value={getValues(featureProductDataPath.value)}
          fullWidth
          label={featureData.name}
          placeholder={EditProductDefaults.PlaceHolder}
          postfixLabel={postfixLabel}
          onChange={(event) => {
            const newValue = event.target.value;
            other.onChange(newValue);
            debouncedHandleInputChange(newValue);
          }}
          maxLength={EditProductDefaults.MaxNumOfChars}
          hasCounter
          dotGroupProps={{
            orangeDot: isShowOrangeDot,
            blueDot: isShowBlueDot,
            greenDot: featureData?.isBusinessMetric,
          }}
        />
      )}
    />
  );
};
